import React, { useState } from 'react';
import Card from '@/components/card';
import Title from '@/components/title';
import { Button, DatePicker, Input, TablePaginationConfig } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import locale from 'antd/es/date-picker/locale/th_TH';
import { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { isFullAccess } from '@/utils/permissionUtils';
import { AxiosResponse } from 'axios';
import CndnFreeFormTable from './components/cdn-table';
import { useAuth } from '@/contexts/AuthContext';
import BusinessUnitSelector from '@/components/selector/business-unit-selector';
import { searchCndnManual } from '@/adapters/cndn-manual';
import {
  ICndnFreeForm,
  ISearchCndnFreeFormReq,
  ISearchCndnFreeFormRes,
} from '@/interfaces/cndnFreeForm';
import SubMenu from '@/components/menu/sub-menu';
import { FreeFormMenu } from '@/configs/menuItems';
import { getPathSegment } from '@/utils/pathUtils';

const { Search } = Input;

const _criteria: ISearchCndnFreeFormReq = {
  orderBy: 'id',
  orderType: 'descend',
  pageSize: 10,
  pageStart: 1,
  search: '',
};

const CndnFreeForm = () => {
  const pathSegment = getPathSegment();
  const navigate = useNavigate();
  const { userInfo } = useAuth();
  const [criteria, setCriteria] = useState<ISearchCndnFreeFormReq>({
    ..._criteria,
    businessUnitId: userInfo?.bu || undefined,
  });

  const { data, refetch, isLoading, isFetching } = useQuery<
    AxiosResponse<ISearchCndnFreeFormRes>,
    Error
  >({
    queryKey: ['search-cndns-manual', criteria],
    queryFn: async () => await searchCndnManual(criteria),
  });

  const onActionOrder = () => {
    refetch();
  };

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<ICndnFreeForm> | SorterResult<ICndnFreeForm>[],
  ) => {
    const newCriteria = { ...criteria };
    if (!Array.isArray(sorter)) {
      newCriteria.orderBy =
        (sorter.column?.dataIndex as keyof ICndnFreeForm) ||
        newCriteria.orderBy;
      newCriteria.orderType = sorter.order || newCriteria.orderType;
    }
    setCriteria({
      ...newCriteria,
      pageStart: Number(pagination.current || 1),
      pageSize: Number(pagination.pageSize || 10),
    });
  };

  const onSearch = (text: string) => {
    setCriteria({
      ...criteria,
      search: text,
      orderBy: 'id',
      orderType: 'descend',
      pageStart: 1,
      pageSize: 10,
    });
  };

  const onRangeChange = (dates: null | (Dayjs | null)[]) => {
    if (dates) {
      setCriteria({
        ...criteria,
        orderBy: 'id',
        orderType: 'descend',
        pageStart: 1,
        pageSize: 10,
        startDate: dates[0]?.toDate(),
        endDate: dates[1]?.toDate(),
      });
    } else {
      setCriteria({
        ...criteria,
        orderBy: 'id',
        orderType: 'descend',
        pageStart: 1,
        pageSize: 10,
        startDate: null,
        endDate: null,
      });
    }
  };

  const perFullAccess = isFullAccess();

  return (
    <>
      <SubMenu active={pathSegment} items={FreeFormMenu} />
      <Card>
        <Title title="ใบเพิ่มหนี้/ ลดหนี้ (Free Form)">
          <div className="flex justify-end gap-3">
            {perFullAccess && (
              <Button
                type="primary"
                onClick={() => navigate(`/cndn-free-form/create`)}
              >
                <PlusOutlined /> เพิ่มใหม่
              </Button>
            )}
          </div>
        </Title>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col md:flex-row justify-between gap-3">
            <div className="flex flex-col md:flex-row justify-between gap-3">
              <BusinessUnitSelector
                handleSelectData={function (id: number): void {
                  setCriteria({ ...criteria, businessUnitId: id });
                }}
                disabled={!!userInfo?.bu}
                value={criteria?.businessUnitId}
              />
              <DatePicker.RangePicker
                size="large"
                locale={locale}
                onChange={onRangeChange}
              />
              <Search
                className="md:max-w-[400px] md:min-w-[400px]"
                placeholder="ค้นหา"
                allowClear
                size="large"
                enterButton="ค้นหา"
                onSearch={onSearch}
              />
            </div>
          </div>
          <CndnFreeFormTable
            criteria={criteria}
            cndn={data?.data || { data: [], totalRecords: 0 }}
            loading={isLoading || isFetching}
            onEditData={item => console.log(item)}
            onDelete={onActionOrder}
            onTableChange={onTableChange}
          />
        </div>
      </Card>
    </>
  );
};

export default CndnFreeForm;
